<template>
  <div class="login-bg">
    <div class="row vh-100 justify-content-center align-items-center mx-0">
      <div class="col-sm-8 col-md-3 offset-md-9 px-0">
        <div class="card pb-1 border vh-100 bg-light position-relative">
          <div class="card-header bg-transparent border-0"></div>
          <div class="logo-badge bg-white shadow p-4 p-md-4 text-center">
            <img src="@/core/assets/logo.png" alt="logo" class="img-fluid log-min-h" />
          </div>
          <div class="shadow-element"></div>
          <div class="card-body px-4">
            <div v-if="errorMessage" class="alert alert-danger mb-3" role="alert">
              <i class="fa fa-exclamation-triangle me-2"></i>
              {{ errorMessage }}
            </div>
            <p class="text-center mt-2 mb-4 h5">
              <strong>{{ $t("landingPage.welcome") }}</strong>
            </p>
            <form>
              <div class="form-group ml-auto">
                <BButton
                  v-if="activeUser == null"
                  variant="primary"
                  class="my-2 my-sm-0 btn-sm btn-block"
                  type="button"
                  @click="login"
                  :disabled="busy"
                  style="background-color: #0054a6; color: white"
                >
                  <font-awesome-icon icon="spinner" spin v-if="busy" />
                  {{ $t("landingPage.ssoLogin") }}</BButton
                >
                <BButton
                  v-else
                  variant="primary"
                  class="my-2 my-sm-0"
                  type="button"
                  @click="login"
                  :disabled="busy"
                  style="background-color: #0054a6; color: white"
                >
                  <font-awesome-icon icon="spinner" spin v-if="busy" />
                  {{ $t("landingPage.continue") }} {{ activeUserName }}
                </BButton>
              </div>
              <div class="row align-self-end h-100">
                <div class="col form-group mt-1 text-left cookie-opacity ml-1">
                  <a href="https://www.coats.com/en/Privacy-and-Cookie-Policy" class="btn-link">{{
                    $t("landingPage.privacyCookies")
                  }}</a>
                </div>
              </div>
            </form>
          </div>
          <div class="card-footer bg-transparent border-0 text-center">
            <p class="mb-0 mt-3 text-center">© Coats Digital {{ year }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="row label-text d-none d-md-block">
      <a href="">{{ $t("landingPage.publishedOn") }} {{ date }}</a>
    </div>
  </div>
</template>

<script lang="ts">
import { User } from "oidc-client";
import { PreLoginApi, UserAdminSettingApi } from "@/api";
import { eventBus } from "@/main";
import { defineComponent } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { BButton } from "bootstrap-vue-next";

export default defineComponent({
  name: "LandingPage",
  components: {
    BButton
  },
  setup() {
    const preLoginApi = new PreLoginApi();
    const userAdminSettingApi = new UserAdminSettingApi();
    return { preLoginApi, userAdminSettingApi };
  },
  data() {
    return {
      busy: false,
      date: "" as any,
      year: 0,
      store: useStore(),
      router: useRouter(),
      errorMessage: "" as string
    };
  },
  created(): void {
    this.preLoginApi.apiPreLoginGet().then((res: { data: any }) => {
      this.date = res.data;
      this.year = new Date().getFullYear();
      eventBus.emit("pageName", "Landing Page");
    });

    this.checkForErrors();
  },
  computed: {
    activeUser(): User | null {
      const user = this.store.getters.oidcUser;
      if (!user) return null;

      const expiresAt = (user as any)?.expires_at;
      const now = Math.floor(Date.now() / 1000);

      if ((user as any)?.expired) return null;
      if (expiresAt && expiresAt <= now) return null;

      return user;
    },

    activeUserName(): string {
      if (this.activeUser as any) {
        return (this.activeUser as any)["given_name"] ?? "";
      }
      return "";
    }
  },
  methods: {
    checkForErrors(): void {
      const urlParams = new URLSearchParams(window.location.search);
      const error = urlParams.get("error");
      const message = urlParams.get("message");

      if (error && message) {
        this.errorMessage = decodeURIComponent(message);
        const url = new URL(window.location.href);
        url.searchParams.delete("error");
        url.searchParams.delete("message");
        window.history.replaceState({}, document.title, url.pathname);
      }
    },

    clearCachedUser(): void {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("oidc.user")) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();
    },

    async login(): Promise<void> {
      this.errorMessage = "";
      this.busy = true;

      try {
        if (this.activeUser) {
          await this.userAdminSettingApi.apiSystemConfigurationGet();
        }

        await this.router.push({ name: "Dashboard" });
      } catch {
        this.clearCachedUser();
        this.busy = false;
        window.location.reload();
      }
    }
  }
});
</script>
<style scoped>
.login-bg {
  background: url(https://coatsdigital-cdn.azureedge.net/images/log-bg.jpg);
  background-size: cover;
  background-repeat: no-repeat;
  background-position: left 25%;
}
.logo-badge {
  flex: 0 0 auto;
  align-items: center;
  background-color: #fff;
  border-right: none;
  margin-top: 17%;
  margin-left: -13px;
}
.log-min-h {
  max-height: 2.625rem;
}
.shadow-element {
  width: 20px;
  height: 20px;
  overflow: hidden;
  position: relative;
  margin-left: -21px;
}
.shadow-element::after {
  content: "";
  background: #58595b;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 110px 40px 0;
  border-color: transparent #58595b transparent;
  position: absolute;
  transform: rotate(45deg) translate(-71%, 0);
}
.label-text {
  position: fixed;
  bottom: 0;
  left: 1rem;
  width: inherit;
  text-align: Left;
  padding-bottom: 10px;
  padding-left: 22px;
}
.label-text a {
  color: #fff;
}
@media (max-width: 768px) {
  .logo-badge {
    margin-right: -13px;
  }
}
.cookie-opacity a {
  color: rgba(0, 84, 166, 0.7);
}
</style>
